import React from 'react';
import './employeeInfoModal.css';
import dummyPhoto from '../../Resources/dummyPhoto.jpg'; // Dummy photo
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPhone, faEnvelope, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons'; // FontAwesome Icons
import { Button, Modal, Row, Col, Image, Spinner } from 'react-bootstrap';
import format from 'date-fns/format';
import { useState, useEffect } from 'react';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';

const EmployeeInfoModal = ({ show, handleClose, employee }) => {
  const {t} = useTranslation();
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false); // State to control modal visibility
  const [currentFileType, setCurrentFileType] = useState('');
  const [loading, setLoading] = useState(false);
  const [companyName, setCompanyName] = useState('Unknown Company');
  const [branchName, setBranchName] = useState('Unknown Branch');
  const [deptName, setDeptName] = useState('Unknown Department');
  const [empRole, setEmpRole] = useState('Unknown Department');


  // Fetch company, branch, and department names when the employee data changes
  useEffect(() => {
    if (employee) {
      const fetchCompanyName = async () => {
        try {
          const response = await axiosInstance.get(`/admin/company-name/${employee.company_id}`);
          setCompanyName(response.data[0]?.company_name || 'Unknown Company');
        } catch (error) {
          console.error('Error fetching company name:', error);
        }
      };

      const fetchBranchName = async () => {
        try {
          const response = await axiosInstance.get(`/admin/branch-name/${employee.branch_id}`);
          setBranchName(response.data[0]?.branch_name || 'Unknown Branch');
        } catch (error) {
          console.error('Error fetching branch name:', error);
        }
      };

      const fetchDeptName = async () => {
        try {
          const response = await axiosInstance.get(`/admin/department-name/${employee.dept_id}`);
          setDeptName(response.data[0]?.dept_name || 'Unknown Department');
        } catch (error) {
          console.error('Error fetching department name:', error);
        }
      };

      const fetchEmpRole = async () => {
        try {
          const response = await axiosInstance.get(`/admin/employee-role/${employee.emp_sap_id}`);
          setEmpRole(response.data[0]?.emp_role || 'Unknown Role');
        } catch (error) {
          console.error('Error fetching department name:', error);
        }
      };

      fetchCompanyName();
      fetchBranchName();
      fetchDeptName();
      fetchEmpRole();
    }
  }, [employee]); // Dependency array, runs when `employee` changes

  if (!employee) return null;

  const handleFilePreview = async (fileType) => {
    setLoading(true); // Show spinner when fetching the file
    try {
      const response = await axiosInstance.get(`/user/trial-get-file/${employee.emp_sap_id}/${fileType}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = URL.createObjectURL(blob);

      setPreviewUrl(url);
      setMimeType(response.headers['content-type']);
      setCurrentFileType(fileType);
      setShowPreviewModal(true); // Show the modal with the preview
      setLoading(false); // Hide spinner when the file is ready

    } catch (error) {
      if (!error.response) {
        // Network error or no response
        toast.error(t("Looks like you are offline. Please check your internet connectivity."));
      } else {
        console.error('Error fetching file:', error);
        toast.error(t('File not uploaded at the time of form submission.'));
        setLoading(false); // Hide spinner in case of an error
      }
    }
  };

  const handleDownload = async (fileType) => {
    try {
      const response = await axiosInstance.get(`/user/trial-get-file/${employee.emp_sap_id}/${fileType}`, {
        responseType: 'blob',
      });

      const contentDisposition = response.headers['content-disposition'];
      const fileNameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const fileName = fileNameMatch ? fileNameMatch[1] : `${fileType}`;

      let fileExtension = 'bin';
      const mimeType = response.headers['content-type'];

      if (mimeType === 'application/pdf') {
        fileExtension = 'pdf';
      } else if (mimeType === 'image/jpeg') {
        fileExtension = 'jpg';
      } else if (mimeType === 'image/png') {
        fileExtension = 'png';
      }

      saveAs(new Blob([response.data], { type: mimeType }), `${fileName}.${fileExtension}`);
    } catch (error) {
      if (!error.response) {
        // Network error or no response
        toast.error(t("Looks like you are offline. Please check your internet connectivity."));
      } else {
        console.error('Error downloading file:', error);
        toast.error('Error downloading file. Please try again.');
      }
    }
  };

  const handleCloseFilePreviewModal = () => {
    setShowPreviewModal(false);
    setPreviewUrl(null);
  };

  return (
    <Modal size='xl' show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("Employee Information")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={4} className="text-center">
            {/* Employee Profile Photo */}
            <Image src={dummyPhoto} roundedCircle fluid className="profile-photo" />
            <h5 className="mt-3">{employee.full_name}</h5>
            <p className="text-muted">{employee.profession}</p>
          </Col>

          <Col md={8}>
            {/* Personal Information */}
            <h6 className="section-title">{t("Personal Information")}</h6>
            <Row>
              <Col md={6}>
                <p><strong>{t("Employee SAP ID")}:</strong> {employee.emp_sap_id}</p>
                <p><strong>{t("Date of Birth")}:</strong> {format(employee.date_of_birth, 'dd/MM/yyyy')}</p>
                <p><strong>{t("Gender")}:</strong> {employee.gender}</p>
                <p><strong>{t("Nationality")}:</strong> {employee.nationality}</p>
                <p><strong>{t("Taxpayer Number")}:</strong> {employee.taxpayer_number}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("Address")}:</strong> {employee.address}, {employee.locality}, {employee.postal_code}</p>
                <p><strong>{t("Place of Birth")}:</strong> {employee.place_of_birth}</p>
                <p><strong>{t("Marital Status")}:</strong> {employee.marital_status}</p>
                <p><strong>{t("No. of Dependents")}:</strong> {employee.no_of_dependents}</p>
                <p><strong>{t("Social Security Number")}:</strong> {employee.social_security_number}</p>
              </Col>
            </Row>

            {/* Professional Information */}
            <h6 className="section-title mt-4">{t("Professional Information")}</h6>
            <Row>
              <Col md={6}>
                <p><strong>{t("Company")}:</strong> {companyName}</p>
                <p><strong>{t("Branch")}:</strong> {branchName}</p>
                <p><strong>{t("Department")}:</strong> {deptName}</p>
                <p><strong>{t("Cost Center")}:</strong> {employee.cost_center}</p>
                <p><strong>{t("Admission Date")}:</strong> {format(employee.admission_date, 'dd/MM/yyyy')}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("Employee Role")}:</strong> {empRole}</p>
                <p><strong>{t("Academic Qualifications")}:</strong> {employee.academic_qualifications}</p>
                <p><strong>{t("HF Function")}:</strong> {employee.hf_function}</p>
                <p><strong>{t("Categories")}:</strong> {employee.categories}</p>
                <p><strong>{t("Remarks")}:</strong> {employee.remarks}</p>
              </Col>
            </Row>

            {/* Contact Information */}
            <h6 className="section-title mt-4">{t("Contact Information")}</h6>
            <Row>
              <Col md={6}>
                <p><FontAwesomeIcon icon={faPhone} /> <strong>{t("Private Mobile")}:</strong> {employee.private_mobile}</p>
                <p><FontAwesomeIcon icon={faEnvelope} /> <strong>{t("Private Email")}:</strong> {employee.private_email}</p>
                <p><FontAwesomeIcon icon={faPhone} /> <strong>{t("Emergency Contact")}:</strong> {employee.emergency_contact_mobile} ({employee.emergency_contact_type})</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("HF Cell Phone")}:</strong> {employee.hf_cell_phone}</p>
                <p><strong>{t("HF Email")}:</strong> {employee.hf_email}</p>
              </Col>
            </Row>

            {/* Accounts Information */}
            <h6 className="section-title mt-4">{t("Accounts Information")}</h6>
            <Row>
              <Col md={6}>
                <p><strong>{t("IBAN")}:</strong> {employee.iban}</p>
                <p><strong>{t("SWIFT")}:</strong> {employee.swift}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("Beneficiary Number")}:</strong> {employee.beneficiary_number}</p>
              </Col>
            </Row>

            {/* Additional Information */}
            <h6 className="section-title mt-4">{t("Additional Information")}</h6>
            <Row>
              <Col md={6}>
                <p><strong>{t("Citizen Card No. / Passport")}:</strong> {employee.citizen_card_no_passport}</p>
                <p><strong>{t("Driving License No.")}:</strong> {employee.driving_license_no}</p>
                <p><strong>{t("Driving License Issue")}:</strong> {format(employee.driving_license_issue_date, 'dd/MM/yyyy')}</p>
              </Col>
              <Col md={6}>
                <p><strong>{t("Expiry CC / Passport")}:</strong> {format(employee.expiry_date_cc_passport, 'dd/MM/yyyy')}</p>
                <p><strong>{t("Driving License Validity")}:</strong> {format(employee.driving_license_validity_date, 'dd/MM/yyyy')}</p>
              </Col>
            </Row>

            {/* Documents Preview */}
            <h6 className="section-title mt-4">{t("Documents")}</h6>
            <Row>
              <Col md={6}>
                <p><button className='download-btn' onClick={() => handleFilePreview('citizenCard')}>{t("Citizen Card / Passport")}</button></p>
                <p><button className='download-btn' onClick={() => handleFilePreview('drivingLicense')}>{t("Driving License")}</button></p>
                <p><button className='download-btn' onClick={() => handleFilePreview('cv')}>{t("Preview CV")}</button></p>
              </Col>
              <Col md={6}>
                <p><button className='download-btn' onClick={() => handleFilePreview('irsForm')}>{t("IRS Form")}</button></p>
                <p><button className='download-btn' onClick={() => handleFilePreview('academicQualifications')}>{t("Academic Qualifications")}</button></p>
              </Col>
            </Row>
          </Col>
        </Row>
        <div>
          <Modal show={showPreviewModal} onHide={handleCloseFilePreviewModal} size="lg" centered>
            <Modal.Header closeButton>
              <Modal.Title>{t("File Preview")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {loading ? (
                <div className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                </div>
              ) : previewUrl && mimeType.startsWith('image/') ? (
                <img
                  src={previewUrl}
                  alt="File Preview"
                  style={{
                    width: '100%',  // Full width within the modal
                    height: 'auto', // Maintain aspect ratio
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <iframe
                  src={previewUrl}
                  title="File Preview"
                  width="100%"
                  height="500px"
                  style={{ border: 'none' }}
                />
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseFilePreviewModal}>{t("Close")}</Button>
              <Button variant="primary" onClick={() => handleDownload(currentFileType)}>{t("Download")}</Button>
            </Modal.Footer>
          </Modal>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>{t("Close")}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmployeeInfoModal;
